import {NestableField} from "../../../../../../components/NestedFormik";
import React from "react";
import {useUIControls} from "../../../../../../components/FormUISwitch";
import {FarsiVerbFormSoundOverride} from "../../../../wordsApi";
import {isValidSoundElement} from "./SoundsField";
import {handleCommaSeparated} from "../WordFormAndLogic";

/**
 * Editor field for `word.data.verb_form_sound_overrides`: per-part
 * pronunciation overrides for generated verb forms that cannot be soundified
 * from the stem spelling (e.g. برو is boro, not the compositional beru).
 *
 * The field uses one line per override rule, with pipe-separated columns:
 *
 *     form | person | stem type | stem | prefix diacritics | stem sounds
 *
 * For example:
 *
 *     present-simple-imperative | 2nd-singular | present | رو | بُ | re, short_o
 *
 * person, stem type and stem may be left empty to match any. At least one of
 * prefix diacritics (replaces the بـ prefix's written/vowel form, which also
 * drives its sound) or stem sounds (comma-separated sound names replacing the
 * stem's pronunciation) must be given. Parts of the word the rule does not
 * mention keep their normal pronunciation.
 */

const COLUMNS = 6;

function parseLines(text: string): string[] {
  return (text || '').split('\n').map(l => l.trim()).filter(l => !!l);
}

function parseLine(line: string): FarsiVerbFormSoundOverride {
  const cols = line.split('|').map(c => c.trim());
  const [form, person, stemType, stem, prefixDiacritics, stemSounds] = cols;
  return {
    form: form,
    person: person || undefined,
    stem_type: stemType || undefined,
    stem: stem || undefined,
    prefix_diacritics: prefixDiacritics || undefined,
    stem_sounds: handleCommaSeparated(stemSounds)
  };
}

export function parseVerbFormSoundOverrides(text: string): FarsiVerbFormSoundOverride[]|undefined {
  const lines = parseLines(text);
  if (!lines.length) {
    return undefined;
  }
  return lines.map(parseLine);
}

export function serializeVerbFormSoundOverrides(overrides?: FarsiVerbFormSoundOverride[]|null): string {
  if (!overrides || !overrides.length) {
    return "";
  }
  return overrides.map(o => [
    o.form,
    o.person || '',
    o.stem_type || '',
    o.stem || '',
    o.prefix_diacritics || '',
    (o.stem_sounds || []).join(', ')
  ].join(' | ')).join('\n');
}

function validateOverrides(value: string) {
  for (const line of parseLines(value)) {
    if (line.split('|').length != COLUMNS) {
      return `Each line needs ${COLUMNS} columns separated by |: form | person | stem type | stem | prefix diacritics | stem sounds`;
    }
    const rule = parseLine(line);
    if (!rule.form) {
      return "The form column is required.";
    }
    if (!rule.prefix_diacritics && !rule.stem_sounds?.length) {
      return "At least one of prefix diacritics or stem sounds is required.";
    }
    for (const sound of rule.stem_sounds || []) {
      if (!isValidSoundElement(sound)) {
        return `${sound} is not a valid sound.`;
      }
    }
  }
}

export function VerbFormSoundOverridesField(props: {}) {
  const UI = useUIControls();

  return <NestableField
    name={"verbFormSoundOverrides"}
    validate={validateOverrides}
    component={UI.Text}
    multiline

    label="Form Sound Overrides"
    description={"One rule per line: form | person | stem type | stem | prefix diacritics | stem sounds. " +
      "E.g.: present-simple-imperative | 2nd-singular | present | رو | بُ | re, short_o"}
  />;
}
